import React, { useEffect, useMemo, useRef } from 'react';

import { graphql, useFragment } from 'react-relay';

import { UserProfilePreview } from '@atlassian/user-profile-preview';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { onPersonKnowledgeCardLoaded } from '../on-person-knowledge-card-loaded';

import type {
	singlePersonKnowledgeCardRelay_reportingLines$data,
	singlePersonKnowledgeCardRelay_reportingLines$key,
} from './__generated__/singlePersonKnowledgeCardRelay_reportingLines.graphql';
import type { singlePersonKnowledgeCardRelay_smarts$key } from './__generated__/singlePersonKnowledgeCardRelay_smarts.graphql';
import type { singlePersonKnowledgeCardRelay_smarts$data } from './__generated__/singlePersonKnowledgeCardRelay_smarts.graphql';
import type { singlePersonKnowledgeCardRelay_user$key } from './__generated__/singlePersonKnowledgeCardRelay_user.graphql';
import type { singlePersonKnowledgeCardRelay_workedOn$data } from './__generated__/singlePersonKnowledgeCardRelay_workedOn.graphql';
import type { singlePersonKnowledgeCardRelay_workedOn$key } from './__generated__/singlePersonKnowledgeCardRelay_workedOn.graphql';
import { confluenceLinksToAbsoluteUrl } from './confluence-links-to-absolute-url';

type Collaborator = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	email?: string | null;
};

type RecentWorkUrls = string[];

type HierarchyUser = {
	accountId: string;
	name: string;
	avatarUrl?: string | null;
	jobTitle?: string | null;
	numberOfDirectReports?: number;
	numberOfTotalReports?: number;
};

type HierarchyData = {
	managers?: HierarchyUser[];
	user: HierarchyUser;
	reports?: HierarchyUser[];
	peers?: HierarchyUser[];
};

type SinglePersonKnowledgeCardRelayProps = Omit<
	React.ComponentProps<typeof UserProfilePreview>,
	'recentWorkData'
> & {
	userFragment?: singlePersonKnowledgeCardRelay_user$key;
	workedOnFragment?: singlePersonKnowledgeCardRelay_workedOn$key;
	smartsFragment?: singlePersonKnowledgeCardRelay_smarts$key;
	reportingLinesFragment?: singlePersonKnowledgeCardRelay_reportingLines$key;
};

type ActivityObjectData = NonNullable<
	NonNullable<singlePersonKnowledgeCardRelay_workedOn$data['edges'][number]>['node']
>['object']['data'];

/**
 * Derives a canonical URL for a recent-activity object, covering every
 * URL-bearing activity type returned by the full profile-page query. Returns
 * an empty string when the object type has no resolvable URL.
 */
function extractActivityUrl(objectData: ActivityObjectData | null | undefined): string {
	if (!objectData) {
		return '';
	}

	switch (objectData.__typename) {
		case 'ConfluencePage':
		case 'ConfluenceBlogPost':
		case 'ConfluenceWhiteboard':
		case 'ConfluenceDatabase':
		case 'ConfluenceEmbed':
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment':
			return objectData.links?.base && objectData.links?.webUi
				? confluenceLinksToAbsoluteUrl(objectData.links.base, objectData.links.webUi)
				: '';
		case 'JiraIssue':
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
		case 'DevOpsDocument':
		case 'DevOpsDesign':
			return objectData.webUrl ?? '';
		case 'TrelloBoard':
		case 'TrelloCard':
			return objectData.trelloUrl ?? '';
		case 'TownsquareGoal':
		case 'TownsquareProject':
		case 'TownsquareComment':
		case 'ExternalDocument':
		case 'DevOpsPullRequestDetails':
		case 'ExternalPullRequest':
		case 'ExternalRemoteLink':
		case 'MercuryFocusArea':
		case 'MercuryPortfolio':
		case 'RadarPosition':
		case 'RadarView':
		case 'CompassComponent':
			return objectData.url ?? '';
		case 'BitbucketPullRequest':
			return objectData.href ?? '';
		case 'LoomVideo':
			return objectData.loomUrl ?? '';
		case 'AssetsObject':
		case 'AssetsObjectType':
		case 'AssetsSchema':
			return objectData.links?.web ?? '';
		default:
			return '';
	}
}

/**
 * Computes the de-duplication key for a recent-activity object, mirroring the
 * full profile page's `deduplicateId` behaviour:
 * - Confluence comments de-duplicate on their container (page/blog/whiteboard),
 *   so multiple comments on the same page collapse to a single entry
 *   (equivalent to `deduplicateId: ['container.id']`).
 * - Jira comments de-duplicate on their parent issue
 *   (equivalent to `deduplicateId: ['issue.id']`).
 * - Everything else de-duplicates on its own URL
 *   (equivalent to `deduplicateId: ['objectId']`).
 *
 * Falls back to the object's own URL when the container/issue is unavailable.
 */
function getActivityDedupeKey(objectData: ActivityObjectData | null | undefined): string {
	const ownUrl = extractActivityUrl(objectData);
	if (!objectData) {
		return ownUrl;
	}
	switch (objectData.__typename) {
		case 'ConfluenceFooterComment':
		case 'ConfluenceInlineComment': {
			const container = objectData.container;
			const containerUrl =
				container && 'links' in container && container.links?.base && container.links?.webUi
					? confluenceLinksToAbsoluteUrl(container.links.base, container.links.webUi)
					: '';
			return containerUrl || ownUrl;
		}
		case 'JiraPlatformComment':
		case 'JiraServiceManagementComment':
			return objectData.issue?.webUrl || ownUrl;
		default:
			return ownUrl;
	}
}

export function mapRelayWorkedOnConnectionToRecentWorkItems(
	data: singlePersonKnowledgeCardRelay_workedOn$data,
): RecentWorkUrls {
	// De-duplicate by the profile page's `deduplicateId` key (comments collapse
	// onto their container/issue), keeping the first occurrence, then resolve
	// each survivor to its display URL.
	const seenKeys = new Set<string>();
	const result: string[] = [];

	for (const edge of data.edges) {
		const objectData = edge?.node?.object?.data;
		const url = extractActivityUrl(objectData);
		if (!url) {
			continue;
		}
		const dedupeKey = getActivityDedupeKey(objectData);
		if (seenKeys.has(dedupeKey)) {
			continue;
		}
		seenKeys.add(dedupeKey);
		result.push(url);
	}

	return result;
}

type singlePersonKnowledgeCardRelay_reportingLinesKd = Extract<
	singlePersonKnowledgeCardRelay_reportingLines$data['reportingLines'],
	{ __typename: 'KnowledgeDiscoveryReportingLines' }
>;

/** `user` / `managers` / `reports` rows — same shape as `singlePersonKnowledgeCardRelay_reportingLines` fragment data. */
function mapKdReportingLinesUser(
	u: singlePersonKnowledgeCardRelay_reportingLinesKd['user'],
): HierarchyData['user'] | null {
	if (!u?.accountIdentifier) {
		return null;
	}
	return {
		accountId: u.accountIdentifier,
		name: u.pii?.name ?? '',
		avatarUrl: u.pii?.picture ?? null,
		jobTitle: u.pii?.extendedProfile?.jobTitle ?? null,
		numberOfDirectReports: u.numberOfDirectReports ?? undefined,
		numberOfTotalReports: u.numberOfTotalReports ?? undefined,
	};
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function mapKnowledgeDiscoveryReportingLinesToHierarchyData(
	data: singlePersonKnowledgeCardRelay_reportingLines$data | null | undefined,
): HierarchyData | undefined {
	if (!data?.reportingLines) {
		return undefined;
	}
	const rl = data.reportingLines;
	if (rl.__typename !== 'KnowledgeDiscoveryReportingLines') {
		return undefined;
	}
	const user = mapKdReportingLinesUser(rl.user);
	if (!user) {
		return undefined;
	}
	const managers = (rl.managers ?? [])
		.map((manager) => mapKdReportingLinesUser(manager))
		.filter((manager): manager is HierarchyData['user'] => manager != null);
	const reports =
		rl.reports
			?.map((report) => mapKdReportingLinesUser(report))
			.filter((report): report is HierarchyData['user'] => report != null) ?? [];
	const peers =
		rl.peers
			?.map((peer) => mapKdReportingLinesUser(peer))
			.filter((peer): peer is HierarchyData['user'] => peer != null) ?? [];
	return {
		managers: managers.length ? managers : undefined,
		user,
		reports: reports.length ? reports : undefined,
		peers: peers.length ? peers : undefined,
	};
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function mapRelaySmartsToCollaborators(
	data: singlePersonKnowledgeCardRelay_smarts$data,
	accountId?: string,
): Collaborator[] {
	const recommended = data.recommendedUser ?? [];
	if (!recommended.length) {
		return [];
	}

	return recommended.reduce<Collaborator[]>((acc, node) => {
		const user = node?.user;
		if (!user) {
			return acc;
		}
		const id = user.accountId;
		if (!id || (accountId && id === accountId)) {
			return acc;
		}
		acc.push({
			accountId: id ?? null,
			name: user.name ?? null,
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.extendedProfile?.jobTitle ?? null,
		});
		return acc;
	}, []);
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export function SinglePersonKnowledgeCardRelay({
	userFragment,
	workedOnFragment,
	smartsFragment,
	reportingLinesFragment,
	...props
}: SinglePersonKnowledgeCardRelayProps): React.JSX.Element {
	const userProfileData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_user on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				accountId: { type: "String!" }
				requestingUserId: { type: "String!" }
				tenantId: { type: "String!" }
			) {
				singleUser(accountId: $accountId, requestingUserId: $requestingUserId, tenantId: $tenantId)
					@optIn(to: "KnowledgeDiscovery Single User") {
					__typename
					... on KnowledgeDiscoverySingleUser {
						user {
							id
							name
							picture
							email
							jobTitle
							department
							location
							organization
							thirdPartyIntegrations {
								externalId
								integrationId
								product
							}
						}
					}
				}
			}
		`,
		userFragment ?? null,
	);

	const workedOnData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_workedOn on ActivityConnection {
				edges {
					node {
						object {
							data {
								__typename
								... on ConfluencePage {
									links {
										base
										webUi
									}
								}
								... on ConfluenceBlogPost {
									links {
										base
										webUi
									}
								}
								... on ConfluenceWhiteboard {
									links {
										base
										webUi
									}
								}
								... on ConfluenceDatabase {
									links {
										base
										webUi
									}
								}
								... on ConfluenceEmbed {
									links {
										base
										webUi
									}
								}
								... on ConfluenceFooterComment {
									links {
										base
										webUi
									}
									container {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
									}
								}
								... on ConfluenceInlineComment {
									links {
										base
										webUi
									}
									container {
										__typename
										... on ConfluencePage {
											links {
												base
												webUi
											}
										}
										... on ConfluenceBlogPost {
											links {
												base
												webUi
											}
										}
										... on ConfluenceWhiteboard {
											links {
												base
												webUi
											}
										}
									}
								}
								... on TownsquareGoal {
									url
								}
								... on TownsquareProject {
									url
								}
								... on TownsquareComment {
									url
								}
								... on JiraIssue {
									webUrl
								}
								... on JiraPlatformComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on JiraServiceManagementComment {
									webUrl
									issue {
										webUrl
									}
								}
								... on TrelloBoard {
									trelloUrl: url
								}
								... on TrelloCard {
									trelloUrl: url
								}
								... on DevOpsDocument {
									webUrl: url
								}
								... on ExternalDocument {
									url
								}
								... on DevOpsPullRequestDetails {
									url
								}
								... on ExternalPullRequest {
									url
								}
								... on DevOpsDesign {
									webUrl: url
								}
								... on ExternalRemoteLink {
									url
								}
								... on MercuryFocusArea {
									url
								}
								... on MercuryPortfolio {
									url
								}
								... on RadarPosition {
									url
								}
								... on RadarView {
									url
								}
								... on CompassComponent {
									url
								}
								... on BitbucketPullRequest {
									href
								}
								... on LoomVideo {
									loomUrl: url
								}
								... on AssetsObject {
									links {
										web
									}
								}
								... on AssetsObjectType {
									links {
										web
									}
								}
								... on AssetsSchema {
									links {
										web
									}
								}
							}
						}
					}
				}
			}
		`,
		workedOnFragment ?? null,
	);

	const smartsData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_smarts on SmartsQueryApi
			@argumentDefinitions(recommendationsQuery: { type: "SmartsRecommendationsQuery!" }) {
				recommendedUser(recommendationsQuery: $recommendationsQuery) {
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		smartsFragment ?? null,
	);

	const reportingLinesData = useFragment(
		graphql`
			fragment singlePersonKnowledgeCardRelay_reportingLines on KnowledgeDiscoveryQueryApi
			@argumentDefinitions(
				aaid: { type: "String!" }
				orgId: { type: "String!" }
				siteId: { type: "String!" }
			) {
				reportingLines(aaid: $aaid, orgId: $orgId, siteId: $siteId)
					@optIn(to: "KnowledgeDiscovery Get reporting lines") {
					__typename
					... on KnowledgeDiscoveryReportingLines {
						user {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						managers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						reports {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
						peers {
							accountIdentifier
							numberOfDirectReports
							numberOfTotalReports
							pii {
								name
								picture
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
			}
		`,
		reportingLinesFragment ?? null,
	);

	const relayHierarchyData = useMemo(
		() => mapKnowledgeDiscoveryReportingLinesToHierarchyData(reportingLinesData),
		[reportingLinesData],
	);

	const recentWorkData = useMemo(() => {
		if (!workedOnData) {
			return undefined;
		}
		const mapped = mapRelayWorkedOnConnectionToRecentWorkItems(workedOnData);
		return mapped.length ? mapped : undefined;
	}, [workedOnData]);

	const relayProfileData = useMemo(() => {
		const result = userProfileData?.singleUser;
		if (!result || result.__typename !== 'KnowledgeDiscoverySingleUser') {
			return undefined;
		}
		const user = result.user;
		if (!user?.id) {
			return undefined;
		}
		return {
			accountId: user.id,
			name: user.name ?? '',
			avatarUrl: user.picture ?? null,
			email: user.email ?? null,
			jobTitle: user.jobTitle ?? null,
			department: user.department ?? null,
			location: user.location ?? null,
			organization: user.organization ?? null,
			thirdPartyIntegrations:
				user.thirdPartyIntegrations?.map((integration) => ({
					externalId: integration.externalId,
					integrationId: integration.integrationId,
					product: integration.product,
				})) ?? null,
		};
	}, [userProfileData]);

	const relayCollaborators = useMemo(() => {
		if (!smartsData) {
			return undefined;
		}
		const mapped = mapRelaySmartsToCollaborators(smartsData, relayProfileData?.accountId);
		return mapped.length ? mapped : undefined;
	}, [smartsData, relayProfileData?.accountId]);

	const { commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();
	const hasFiredLoadedEventRef = useRef(false);

	// Fire the `aiAnswerPersonKnowledgeCard loaded` track event once per mount, after Relay
	// resolves. This component only renders inside a Suspense+ErrorBoundary, so reaching here
	// always means a successful data load — errors are caught by the boundary above.
	useEffect(() => {
		if (hasFiredLoadedEventRef.current) {
			return;
		}
		hasFiredLoadedEventRef.current = true;
		fireAnalyticsEvent(
			onPersonKnowledgeCardLoaded({
				...commonAttributes,
				personCardVariant: 'singlePerson',
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<UserProfilePreview
			{...props}
			profileData={relayProfileData ?? props.profileData}
			hierarchyData={relayHierarchyData ?? props.hierarchyData}
			recentWorkData={recentWorkData}
			collaborators={relayCollaborators ?? props.collaborators}
		/>
	);
}
